import React, { useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import DeviceCapturePanel from "./DeviceCapturePanel";
import { useTraffic } from "../context/TrafficContext";

// Proxy API base — HTTPS tunnel in production, direct HTTP on local dev
const PROXY_API_BASE = process.env.REACT_APP_PROXY_API_URL
    ? process.env.REACT_APP_PROXY_API_URL.replace(/\/$/, '')
    : null; // null = use ip:8001 directly (local dev)

/* ════════════════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════════════════ */
// How the enrollment key is kept alive while the dialog sits open. The key's
// TTL is set by the proxy (enrollKeys.js, 15 minutes today) and comes back on
// every mint, so these only decide how early we replace it.
const REMINT_LEAD_MS  = 60_000;  // re-mint a minute before the current key dies
const MIN_REMINT_MS   = 30_000;  // floor, so a short TTL cannot spin the timer
const ENROLL_RETRY_MS = 15_000;  // a failed mint is usually transient (API restart)

const ENROLL_PENDING = { key: '', status: 'pending', enrollmentId: '', owner: '' };

/**
 * The query string every setup URL this modal hands a device must carry.
 *
 * `ssid` is the DASHBOARD SESSION, not the Wi-Fi name (that is `wifi`). It ties
 * the device's traffic back to this dashboard session; without it the proxy
 * stamps a synthetic Direct_* session that the dashboard's own filter
 * (viewerSsid === r.ssid) then drops, so the device's traffic silently never
 * appears. `k` is the enrollment key that lets the downloaded profile carry the
 * proxy credentials. `allow=ip` (Android QR only) asks the proxy to open a
 * time-boxed password-free pass for the phone's public IP — Android apps cannot
 * send proxy credentials at all (proxy-server/ipAllow.js). It only works with `k`.
 *
 * Exported because it is the one place that knows the full param set: a surface
 * that builds its own URL drops a param sooner or later, and a dropped param is
 * invisible until a device fails to show up.
 */
export function buildSetupQuery({ wifiName, deviceName, sessionId, enrollKey, allowIp = false } = {}) {
    return [
        wifiName   ? `wifi=${encodeURIComponent(wifiName)}`     : '',
        deviceName ? `device=${encodeURIComponent(deviceName)}` : '',
        sessionId  ? `ssid=${encodeURIComponent(sessionId)}`    : '',
        enrollKey  ? `k=${encodeURIComponent(enrollKey)}`       : '',
        enrollKey && allowIp ? 'allow=ip' : '',
    ].filter(Boolean).join('&');
}

/* Live Android per-IP passes (proxy-server/ipAllow.js) with Revoke. Polls while
 * mounted so a phone that just scanned shows up without reopening the dialog. */
function IpPassList({ apiBase }) {
    const [passes, setPasses] = useState(null);
    const [err, setErr] = useState('');
    const load = React.useCallback(async () => {
        try {
            const r = await fetch(`${apiBase}/proxy/ip-allow`, { signal: AbortSignal.timeout(5000) });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setPasses(Array.isArray(d?.passes) ? d.passes : []);
            setErr('');
        } catch (e) { setErr(e?.message || 'unavailable'); }
    }, [apiBase]);
    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);
    const revoke = async (ip) => {
        try { await fetch(`${apiBase}/proxy/ip-allow/${encodeURIComponent(ip)}`, { method: 'DELETE' }); } catch { /* list reload shows the truth */ }
        load();
    };
    return (
        <div style={{ marginTop: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Networks with a pass</div>
            {err && <div style={{ color: 'var(--muted)' }}>Could not load passes ({err}).</div>}
            {!err && passes && passes.length === 0 && <div style={{ color: 'var(--muted)' }}>None — scan the QR on the phone to open one.</div>}
            {(passes || []).map((p) => (
                <div key={p.ip} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                    <code>{p.ip}</code>
                    <span style={{ color: 'var(--muted)' }}>{p.device ? `${p.device} · ` : ''}until {new Date(p.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <button type="button" onClick={() => revoke(p.ip)} style={{ marginLeft: 'auto', fontSize: 11, cursor: 'pointer' }}>Revoke</button>
                </div>
            ))}
        </div>
    );
}

export default function ConnectDeviceModal({
    isOpen,
    onClose,
    serverIp: initialIp,
    port: initialPort = 8000,
}) {
    const [tab, setTab] = useState("ios"); // 'ios' | 'android' | 'appletv' | 'androidtv'
    const [ip, setIp]   = useState(initialIp);
    const [port, setPort] = useState(initialPort);

    const { sessionId } = useTraffic();
    const [wifiName, setWifiName]     = useState("");
    const [deviceName, setDeviceName] = useState("Device-1");


    // True once the operator edits the host/port fields by hand, which stops
    // /proxy/info from overwriting what they typed. A ref, not state, so it
    // never re-runs the fetch effect below.
    const addrTouched = useRef(false);

    /* ── sync props ── */
    useEffect(() => {
        if (isOpen) { setIp(initialIp); setPort(initialPort); addrTouched.current = false; }
    }, [isOpen, initialIp, initialPort]);

    // The Wi-Fi name describes where the DEVICE is right now, and a browser cannot
    // read it — so it has to be typed, and a stale one is silently wrong.
    //
    // This component stays mounted for the life of the page (the parent renders it
    // unconditionally and just toggles `isOpen`), so the field used to keep the
    // SSID from the previous enrollment indefinitely. Reopen the dialog on a
    // different network and the QR still scoped the profile to the old one; iOS
    // installs that without complaint, applies it only on a network the device is
    // not joined to, and the capture comes up empty with nothing reporting a
    // fault. Clearing costs a retype per enrollment and removes a whole class of
    // silent failure. Device name is deliberately NOT cleared — being wrong about
    // a label is harmless.
    useEffect(() => {
        if (isOpen) setWifiName("");
    }, [isOpen]);

    /* ── Resolve API base URL ── */
    const apiBase = PROXY_API_BASE || `http://${ip}:8001`;

    // Proxy host shown to devices — fetched dynamically from /proxy/info.
    //
    // THE PROXY'S OWN ANSWER IS THE ONLY TRUSTWORTHY ADDRESS HERE. `initialIp`
    // arrives from TrafficContext, which on a public dashboard falls back to
    // window.location.hostname — in production that is `adlensfx.com`, the one
    // host that CANNOT work as a proxy: it resolves to Cloudflare, and
    // Cloudflare does not proxy port 8000, so a device pointed at it times out
    // on every request with nothing reaching the droplet. /proxy/info returns
    // PUBLIC_HOST (the droplet IP), which is what the device must be given, so
    // adopt it into the editable fields rather than only using it for the QR —
    // otherwise the header and the manual-setup steps disagree with the QR code
    // and whoever types the values by hand silently gets a dead proxy.
    const [proxyInfo, setProxyInfo] = useState(null);
    useEffect(() => {
        if (!isOpen) return;
        fetch(`${apiBase}/proxy/info`, { signal: AbortSignal.timeout(3000) })
            .then(r => r.json())
            .then(d => {
                setProxyInfo(d);
                if (!addrTouched.current && d?.host) {
                    setIp(d.host);
                    if (d.proxyPort) setPort(d.proxyPort);
                }
            })
            .catch(() => setProxyInfo(null));
    }, [isOpen, apiBase]);

    // Device-enrollment key (see proxy-server/enrollKeys.js). Minted through the
    // authenticated API so the profile the device downloads can carry the proxy
    // credentials — without it the tester types PROXY_TOKEN on a phone keyboard.
    //
    // RE-MINT, AND SAY SO WHEN IT FAILS. The key expires (15 min) and the QR is
    // drawn from whatever key is in hand, so a dialog left open longer than that
    // used to hand the device a dead one. The device's profile then installs with
    // no credentials, iOS reports "Authentication: off", and every CONNECT gets a
    // 407 — which presents as a broken proxy rather than as a stale QR code. So
    // hold a live key for as long as the dialog is open, and surface the failure
    // instead of degrading into a credential-less profile without saying anything.
    //
    // Re-minting does not revoke the key already on screen: a QR scanned a moment
    // before a refresh stays redeemable for the rest of its own TTL.
    const [enroll, setEnroll] = useState(ENROLL_PENDING);
    useEffect(() => {
        if (!isOpen) { setEnroll(ENROLL_PENDING); return; }

        let cancelled = false;
        let timer = null;
        const again = (ms, fn) => { clearTimeout(timer); timer = setTimeout(fn, ms); };

        const mint = async () => {
            try {
                const r = await fetch(`${apiBase}/proxy/enroll`, {
                    method: 'POST',
                    signal: AbortSignal.timeout(5000),
                });
                if (cancelled) return;
                // 409 = the proxy runs with no PROXY_TOKEN. There is no credential to
                // embed and the device needs none, so this is a healthy state, not a
                // failure — the profile keeps its old prompt-free shape.
                if (r.status === 409) { setEnroll({ key: '', status: 'not_required' }); return; }
                // 403 = per-user isolation is on and this dashboard session is a static-token
                // (machine) caller with no account to own the device. Retrying cannot help.
                if (r.status === 403) { setEnroll({ key: '', status: 'requires_user' }); return; }
                if (!r.ok) throw new Error(`enroll ${r.status}`);
                const d = await r.json();
                if (cancelled) return;
                if (!d?.key) throw new Error('enroll returned no key');
                // enrollmentId/owner (per-user proxy): the device that redeems this key is
                // bound to `owner`; the secret never leaves the proxy except in the profile.
                setEnroll({ key: d.key, status: 'ok', enrollmentId: d.enrollmentId || '', owner: d.owner || '' });
                const ttl = Number(d.ttlMs) || 0;
                again(Math.max(MIN_REMINT_MS, ttl - REMINT_LEAD_MS), mint);
            } catch {
                if (cancelled) return;
                setEnroll({ key: '', status: 'error' });
                again(ENROLL_RETRY_MS, mint);
            }
        };

        mint();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [isOpen, apiBase]);

    const proxyDisplayHost = proxyInfo?.host || ip;
    const proxyDisplayPort = proxyInfo?.proxyPort || port;

    if (!isOpen) return null;

    /* ── Setup URLs — see buildSetupQuery for what each param carries and why ── */
    const setupQuery  = buildSetupQuery({ wifiName, deviceName, sessionId, enrollKey: enroll.key });
    const connectBase = `http://${proxyDisplayHost}:${proxyDisplayPort}/connect`;
    const connectUrl  = setupQuery ? `${connectBase}?${setupQuery}` : connectBase;

    const setupUrl = wifiName && deviceName ? connectUrl : "";
    // Android QR: same params plus allow=ip, so scanning it opens the per-IP pass.
    const androidQuery = buildSetupQuery({ wifiName, deviceName, sessionId, enrollKey: enroll.key, allowIp: true });
    const androidConnectUrl = androidQuery ? `${connectBase}?${androidQuery}` : connectBase;
    const pacUrl   = `http://${proxyDisplayHost}:${proxyDisplayPort}/proxy.pac`;

    /* ── Apple TV profile URL — same wifi-scoped flow as iOS, tvos endpoint ── */
    const tvosSetupUrl = wifiName && deviceName ? `${connectBase}/tvos.mobileconfig?${setupQuery}` : "";

    // Shown whenever the profile is about to go out without credentials for a
    // reason the operator can act on. A 409 ('not_required') is not one: it means
    // the proxy has no PROXY_TOKEN, so no credential exists to embed.
    const enrollFailed = enroll.status === 'error';

    /* ── Android TV adb commands ── */
    const adbSetProxy = `adb shell settings put global http_proxy ${proxyDisplayHost}:${proxyDisplayPort}`;
    const adbClearProxy = `adb shell settings put global http_proxy :0`;

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.modal} onClick={e => e.stopPropagation()}>

                {/* ── Header ── */}
                <div style={S.header}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 18 }}>Connect Device</h2>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                            Proxy: {proxyDisplayHost}:{proxyDisplayPort}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                            style={{ ...S.input, width: 120, fontSize: 12 }}
                            value={ip} onChange={e => { addrTouched.current = true; setIp(e.target.value); }}
                            title="Proxy Host"
                        />
                        <input
                            style={{ ...S.input, width: 64, fontSize: 12 }}
                            value={port} onChange={e => { addrTouched.current = true; setPort(e.target.value); }}
                            title="Proxy Port"
                        />
                        <button style={S.closeBtn} onClick={onClose}>×</button>
                    </div>
                </div>

                {/* ── Tabs ── */}
                <div style={S.tabs}>
                    {[
                        { key: "ios",       label: "🍎 iOS"        },
                        { key: "android",   label: "🤖 Android"    },
                        { key: "appletv",   label: "📺 Apple TV"   },
                        { key: "androidtv", label: "📺 Android TV" },
                    ].map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            style={{ ...S.tab, ...(tab === t.key ? S.activeTab : {}) }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Per-user isolation: who the device will be bound to, or why it cannot be. */}
                {enroll.status === 'ok' && enroll.owner && (
                    <div data-testid="enroll-owner" style={{ padding: '6px 18px', fontSize: 11, color: 'var(--muted)', borderBottom: '1px solid var(--line)' }}>
                        A device that redeems this QR is enrolled to <b>{enroll.owner}</b>; only you will see its traffic.
                    </div>
                )}
                {enroll.status === 'requires_user' && (
                    <div data-testid="enroll-requires-user" style={{ ...S.enrollWarn, margin: '8px 18px 0' }}>
                        This dashboard session is using a shared script token, so a device cannot be enrolled to an account. Sign in again with your own login and reopen this dialog.
                    </div>
                )}

                {/* ── Body ── */}
                <div style={S.body}>

                    {/* ════ iOS TAB ════ */}
                    {tab === "ios" && (
                        <div>
                            <div style={S.fieldGroup}>
                                <label style={S.label}>Wi-Fi Network Name (Exact) <span style={{ color: "red" }}>*</span></label>
                                <input
                                    style={S.input}
                                    value={wifiName}
                                    onChange={e => setWifiName(e.target.value)}
                                    placeholder="e.g. Office_WiFi"
                                />
                                <div style={S.hint}>Required so the iOS Profile targets the correct network.</div>
                            </div>

                            <div style={S.fieldGroup}>
                                <label style={S.label}>Device Name <span style={{ color: "red" }}>*</span></label>
                                <input
                                    style={S.input}
                                    value={deviceName}
                                    onChange={e => setDeviceName(e.target.value)}
                                    placeholder="e.g. QA-iPhone-1"
                                />
                                <div style={S.hint}>Used to identify this device in the dashboard.</div>
                            </div>

                            <div style={{ textAlign: "center", margin: "16px 0" }}>
                                {setupUrl ? (
                                    <div style={{ display: "inline-block", background: "var(--card)", padding: 12, borderRadius: 8, border: "1px solid var(--line)" }}>
                                        <QRCode value={setupUrl} size={160} />
                                        <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>Scan to install proxy profile</div>
                                    </div>
                                ) : (
                                    <div style={S.qrPlaceholder}>
                                        Enter Wi-Fi &amp; Device Name<br />to generate QR code
                                    </div>
                                )}
                            </div>

                            {setupUrl && (
                                <div style={S.urlBox}>
                                    <a href={setupUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)", wordBreak: "break-all" }}>
                                        {setupUrl}
                                    </a>
                                </div>
                            )}

                            {enrollFailed && (
                                <div style={S.enrollWarn}>
                                    ⚠️ <b>Could not reach the enrollment API — this profile will install without
                                    proxy credentials.</b> iOS will show <b>Authentication: off</b> under the Wi-Fi
                                    proxy, and the proxy will answer the device with a 407, so no traffic is
                                    captured. Check <code>API_TOKEN</code> on the proxy and reopen this dialog;
                                    retrying in the background.
                                </div>
                            )}

                            <ol style={S.steps}>
                                <li>Scan the QR with iPhone Camera — open link in <b>Safari</b>.</li>
                                <li>Tap <b>Allow</b> → then go to <b>Settings → General → VPN &amp; Device Management</b> → <b>Install</b>.</li>
                                <li style={{ color: "var(--warn-text)", fontWeight: 600 }}>Go to <b>Settings → General → About → Certificate Trust Settings</b> → enable full trust for <b>AdLens FX CA</b>.<br/><span style={{ fontWeight: 400, fontSize: 11 }}>⚠️ This step is required for HTTPS — without it the proxy won't work.</span></li>
                            </ol>
                        </div>
                    )}

                    {/* ════ ANDROID TAB ════ */}
                    {tab === "android" && (
                        <div>
                            {/* Wi-Fi proxy vs USB adb — adb is Android-only, so the choice lives here */}
                            <div style={{ paddingBottom: 12, marginBottom: 12, borderBottom: "1px solid var(--line)" }}>
                                <DeviceCapturePanel />
                            </div>
                            {proxyInfo?.authEnabled && (
                                <div style={S.authBox}>
                                    <div style={S.authTitle}>🔓 Apps need a network pass</div>
                                    <div style={S.authNote}>
                                        Android's Wi-Fi settings have no proxy password field, so apps (and their ad SDKs) can't log in.
                                        Instead, <b>scan the QR below with this phone first</b> — it lets the phone's current network through
                                        without a password for a few hours. Then set the proxy (Option A or B). Scan again to extend, or after
                                        switching networks.
                                    </div>
                                    <div style={{ ...S.authNote, color: 'var(--warn-text)', fontWeight: 600 }}>
                                        ⚠️ Everyone on that same network shares the pass while it lasts — don't do this on public Wi-Fi (hotel, café, airport).
                                    </div>
                                    <div data-testid="android-takeover-note" style={S.authNote}>
                                        The pass is tied to your account. If another account enrolls a device from the same network address, the pass moves to them and a warning appears on this dashboard; re-enroll to take it back.
                                    </div>
                                    {enrollFailed && (
                                        <div style={{ ...S.authNote, color: '#b91c1c' }}>
                                            The setup key could not be minted, so this QR will not open a pass — reopen this dialog.
                                        </div>
                                    )}
                                    <IpPassList apiBase={apiBase} />
                                </div>
                            )}
                            <div style={S.section}>
                                <div style={S.sectionTitle}>Option A — Auto-Config (PAC) <span style={S.recommended}>Recommended</span></div>
                                <div style={S.urlBox} onClick={() => navigator.clipboard?.writeText(pacUrl)} title="Click to copy">
                                    <span style={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>{pacUrl}</span>
                                    <span style={{ marginLeft: 8, fontSize: 11, color: "var(--primary)", cursor: "pointer" }}>Copy</span>
                                </div>
                                <ol style={S.steps}>
                                    <li>Go to <b>Settings → Wi-Fi</b>, long-press your network → <b>Modify</b>.</li>
                                    <li>Set Proxy to <b>Auto-Config</b>, paste the URL above.</li>
                                    <li>Download &amp; install the <a href={`http://${proxyDisplayHost}:${port}/connect/ca.pem`} style={{ color: "var(--primary)" }}>CA Certificate</a>.</li>
                                </ol>
                            </div>

                            <div style={{ ...S.section, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                                <div style={S.sectionTitle}>Option B — Manual Proxy</div>
                                <div style={S.manualRow}>
                                    <span style={S.manualLabel}>Host</span>
                                    <span style={S.manualValue}>{proxyDisplayHost}</span>
                                </div>
                                <div style={S.manualRow}>
                                    <span style={S.manualLabel}>Port</span>
                                    <span style={S.manualValue}>{proxyDisplayPort}</span>
                                </div>
                                <ol style={{ ...S.steps, marginTop: 10 }}>
                                    <li>Go to <b>Settings → Wi-Fi</b>, long-press your network → <b>Modify</b>.</li>
                                    <li>Set Proxy to <b>Manual</b>, enter host &amp; port above.</li>
                                    <li>Install the <a href={`http://${proxyDisplayHost}:${port}/connect/ca.pem`} style={{ color: "var(--primary)" }}>CA Certificate</a>.</li>
                                </ol>
                            </div>

                            <div style={{ textAlign: "center", marginTop: 12 }}>
                                <div style={{ display: "inline-block", background: "var(--card)", padding: 10, borderRadius: 8, border: "1px solid var(--line)" }}>
                                    <QRCode value={androidConnectUrl} size={160} />
                                </div>
                                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 6 }}>Scan on the phone to open the setup page{proxyInfo?.authEnabled ? ' and its network pass' : ''}</div>
                                <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--muted)", marginTop: 4, wordBreak: "break-all" }}>
                                    {androidConnectUrl}
                                </div>
                                <div style={{
                                    marginTop: 10, padding: '8px 12px', borderRadius: 6,
                                    background: 'var(--warn-bg)', border: '1px solid var(--warn-border)',
                                    fontSize: 11, color: 'var(--warn-text)', textAlign: 'left', lineHeight: 1.6,
                                }}>
                                    💡 <b>If QR scan doesn't open automatically</b> — configure proxy first (steps above), then manually type the URL above into <b>Chrome</b> on the device to reach the setup page and install the CA certificate.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ════ APPLE TV TAB ════ */}
                    {tab === "appletv" && (
                        <div>
                            <div style={S.noteBox}>
                                📺 Apple TV has no browser and no on-device proxy UI. Open this dashboard on your <b>Mac</b> — the TV can't scan the QR — and install the profile via <b>Apple Configurator</b>.
                            </div>

                            <div style={S.fieldGroup}>
                                <label style={S.label}>Wi-Fi Network Name (Exact) <span style={{ color: "red" }}>*</span></label>
                                <input
                                    style={S.input}
                                    value={wifiName}
                                    onChange={e => setWifiName(e.target.value)}
                                    placeholder="e.g. Office_WiFi"
                                />
                                <div style={S.hint}>Required — this profile is Wi-Fi-scoped and only proxies traffic on this exact SSID.</div>
                            </div>

                            <div style={S.fieldGroup}>
                                <label style={S.label}>Device Name <span style={{ color: "red" }}>*</span></label>
                                <input
                                    style={S.input}
                                    value={deviceName}
                                    onChange={e => setDeviceName(e.target.value)}
                                    placeholder="e.g. QA-AppleTV-1"
                                />
                                <div style={S.hint}>Used to identify this device in the dashboard.</div>
                            </div>

                            {tvosSetupUrl && (
                                <div style={{ textAlign: "center", margin: "12px 0" }}>
                                    <a href={tvosSetupUrl} style={S.downloadBtn}>⬇ Download Apple TV Profile</a>
                                    <div style={S.urlBox}>
                                        <a href={tvosSetupUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)", wordBreak: "break-all" }}>
                                            {tvosSetupUrl}
                                        </a>
                                    </div>
                                </div>
                            )}

                            <ol style={S.steps}>
                                <li>Install <b>Apple Configurator</b> from the Mac App Store.</li>
                                <li>Download the Apple TV profile above onto your Mac.</li>
                                <li>Pair the Apple TV in Configurator — <b>4K</b> pairs over the same Wi-Fi; <b>HD</b> requires a <b>USB-C</b> cable.</li>
                                <li>In Configurator, select the Apple TV → <b>Add</b> → <b>Profiles</b> → choose the downloaded profile.</li>
                                <li style={{ color: "var(--warn-text)", fontWeight: 600 }}>Confirm the Apple TV is on the <b>same Wi-Fi SSID</b> entered above.<br/><span style={{ fontWeight: 400, fontSize: 11 }}>⚠️ Unsupervised profile — Wi-Fi-scoped only. A supervised any-network variant requires Configurator "Prepare" (device erase) and is a future option.</span></li>
                            </ol>
                        </div>
                    )}

                    {/* ════ ANDROID TV TAB ════ */}
                    {tab === "androidtv" && (
                        <div>
                            <div style={S.noteBox}>
                                📺 Open this dashboard on your <b>Mac</b> or PC — the TV can't scan the QR, and most Android TVs have no proxy field in Wi-Fi settings.
                            </div>

                            <div style={S.warnBox}>
                                ⚠️ <b>Android 7+ CA trust:</b> apps don't trust user-installed CA certificates by default. A TV app's HTTPS may not be interceptable unless the app is debuggable or explicitly opts in.
                            </div>

                            <div style={S.section}>
                                <div style={S.sectionTitle}>Option A — adb <span style={S.recommended}>Recommended</span></div>
                                <div style={S.urlBox}>
                                    <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>adb connect &lt;tv-ip&gt;:5555</span>
                                </div>
                                <div style={S.urlBox} onClick={() => navigator.clipboard?.writeText(adbSetProxy)} title="Click to copy">
                                    <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{adbSetProxy}</span>
                                    <span style={{ marginLeft: 8, fontSize: 11, color: "var(--primary)", cursor: "pointer" }}>Copy</span>
                                </div>
                                <ol style={S.steps}>
                                    <li>Connect: <code>adb connect &lt;tv-ip&gt;:5555</code></li>
                                    <li>Set proxy (tap box above to copy).</li>
                                    <li>Clear/reset when done: <code>{adbClearProxy}</code></li>
                                </ol>
                            </div>

                            <div style={{ ...S.section, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                                <div style={S.sectionTitle}>Option B — Manual Proxy (if the TV exposes it)</div>
                                <div style={S.manualRow}>
                                    <span style={S.manualLabel}>Host</span>
                                    <span style={S.manualValue}>{proxyDisplayHost}</span>
                                </div>
                                <div style={S.manualRow}>
                                    <span style={S.manualLabel}>Port</span>
                                    <span style={S.manualValue}>{proxyDisplayPort}</span>
                                </div>
                                <div style={{ ...S.urlBox, marginTop: 10 }} onClick={() => navigator.clipboard?.writeText(pacUrl)} title="Click to copy">
                                    <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{pacUrl}</span>
                                    <span style={{ marginLeft: 8, fontSize: 11, color: "var(--primary)", cursor: "pointer" }}>Copy</span>
                                </div>
                                <ol style={{ ...S.steps, marginTop: 10 }}>
                                    <li>In Wi-Fi settings, set Proxy to <b>Manual</b> (host/port above) or <b>Auto-Config</b> (PAC URL above) — if available.</li>
                                </ol>
                            </div>

                            <div style={{ ...S.section, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                                <div style={S.sectionTitle}>Install CA Certificate</div>
                                <ol style={S.steps}>
                                    <li>Sideload/push: <code>adb push ca.pem /sdcard/Download/</code>, downloaded from <a href={`http://${proxyDisplayHost}:${port}/connect/ca.pem`} style={{ color: "var(--primary)" }}>here</a>.</li>
                                    <li>Settings → Security → Install from storage (path varies by TV OEM launcher).</li>
                                </ol>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ══════════════════ Styles ══════════════════ */
const S = {
    overlay: {
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999,
    },
    modal: {
        background: "var(--card, #fff)",
        borderRadius: 14,
        width: 520, maxWidth: "95vw", maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        border: "1px solid var(--line, var(--line))",
        color: "var(--ink, #111)",
        overflow: "hidden",
    },
    header: {
        padding: "14px 18px",
        borderBottom: "1px solid var(--line, var(--line))",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 10,
    },
    closeBtn: {
        border: "none", background: "transparent",
        fontSize: 22, cursor: "pointer", color: "var(--muted)", lineHeight: 1,
    },
    tabs: {
        display: "flex",
        borderBottom: "1px solid var(--line, var(--line))",
        background: "var(--bg, #f9fafb)",
    },
    tab: {
        flex: 1, padding: "11px 4px",
        border: "none", background: "none",
        cursor: "pointer", fontSize: 13, color: "var(--muted)",
        borderBottom: "2px solid transparent",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
    },
    activeTab: {
        color: "var(--primary)", borderBottom: "2px solid var(--primary)", fontWeight: 700,
        background: "var(--card, #fff)",
    },
    body: {
        padding: 18, overflowY: "auto", flex: 1,
    },
    input: {
        padding: "7px 10px", borderRadius: 6,
        border: "1px solid var(--line, var(--line))",
        background: "var(--bg, #f9fafb)", color: "var(--ink, #111)",
        fontSize: 14, width: "100%", boxSizing: "border-box",
    },
    fieldGroup: { marginBottom: 14 },
    label: { display: "block", fontSize: 12, fontWeight: 700, color: "var(--ink)", marginBottom: 4 },
    hint: { fontSize: 11, color: "var(--muted)", marginTop: 3 },
    qrPlaceholder: {
        height: 160, width: 160, display: "inline-flex",
        alignItems: "center", justifyContent: "center",
        background: "var(--bg, #f5f5f7)", color: "var(--muted)",
        borderRadius: 10, padding: 16, textAlign: "center",
        fontSize: 12, border: "1px dashed var(--line)",
    },
    urlBox: {
        background: "var(--bg, #f5f5f7)", padding: "8px 12px",
        borderRadius: 6, marginBottom: 10,
        border: "1px solid var(--line, var(--line))",
        fontSize: 12, cursor: "pointer", wordBreak: "break-all",
        display: "flex", justifyContent: "space-between", alignItems: "center",
    },
    steps: {
        fontSize: 13, lineHeight: 1.8, color: "var(--ink)",
        paddingLeft: 18, margin: "10px 0 0",
    },
    section: { marginBottom: 16 },
    sectionTitle: { fontWeight: 700, fontSize: 13, marginBottom: 8 },
    recommended: {
        background: "var(--success-bg)", color: "var(--success-text)",
        fontSize: 10, fontWeight: 600, padding: "1px 6px",
        borderRadius: 999, marginLeft: 6,
    },
    manualRow: {
        display: "flex", gap: 12, alignItems: "center",
        marginBottom: 4, fontSize: 13,
    },
    manualLabel: { color: "var(--muted)", width: 36 },
    manualValue: { fontFamily: "monospace", fontWeight: 700, fontSize: 14 },

    /* TV tabs — Mac-only note + Android-7 CA-trust caveat */
    noteBox: {
        background: "var(--info-bg)", border: "1px solid var(--info-border)",
        borderRadius: 8, padding: "10px 12px",
        marginBottom: 14, fontSize: 12.5, color: "var(--info-text)", lineHeight: 1.5,
    },
    warnBox: {
        background: "var(--warn-bg)", border: "1px solid var(--warn-border)",
        borderRadius: 8, padding: "10px 12px",
        marginBottom: 14, fontSize: 12.5, color: "var(--warn-text)", lineHeight: 1.5,
    },
    downloadBtn: {
        display: "block", width: "100%", boxSizing: "border-box",
        background: "var(--primary)", color: "#fff",
        padding: "12px", borderRadius: 10,
        fontSize: 14, fontWeight: 600, textAlign: "center",
        textDecoration: "none", cursor: "pointer",
    },

    /* auth panel — adapts to dark/light theme via CSS vars */
    enrollWarn: {
        background: "var(--warn-bg)",
        border: "1px solid var(--warn-border)",
        borderRadius: 6,
        padding: "9px 12px",
        margin: "10px 0 4px",
        fontSize: 11,
        lineHeight: 1.6,
        color: "var(--warn-text)",
    },
    authBox: {
        background: "var(--bg, #f9fafb)",
        border: "1px solid var(--line, var(--line))",
        borderLeft: "3px solid var(--primary)",
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 14,
        fontSize: 12,
    },
    authTitle: {
        fontWeight: 700,
        marginBottom: 8,
        fontSize: 12,
        color: "var(--ink, #0f172a)",
        letterSpacing: "0.01em",
    },
    authRow: {
        display: "flex", gap: 8, alignItems: "center", marginBottom: 6,
    },
    authLabel: {
        color: "var(--muted, #64748b)",
        minWidth: 72,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    authCode: {
        background: "var(--line, #e2e8f0)",
        color: "var(--ink, #0f172a)",
        padding: "2px 10px",
        borderRadius: 5,
        fontSize: 12,
        fontFamily: "monospace",
        fontWeight: 700,
        flex: 1,
    },
    authNote: {
        fontSize: 11,
        color: "var(--muted, #64748b)",
        marginTop: 6,
        lineHeight: 1.5,
    },
};
